import DashboardIcon from "@mui/icons-material/Dashboard";
import BarChartIcon from "@mui/icons-material/BarChart";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import RuleIcon from "@mui/icons-material/Rule";
import AlertLogIcon from "@mui/icons-material/PrivacyTipOutlined";
import AlertIcon from "@mui/icons-material/AlarmOnOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import DataObjectIcon from "@mui/icons-material/DataObject";
import PasswordIcon from "@mui/icons-material/Password";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import GroupsIcon from "@mui/icons-material/Groups";
import SendIcon from "@mui/icons-material/SendOutlined";
import StorageIcon from "@mui/icons-material/Storage";
import { AuthContextData } from "providers/Login";
import { ALL_ROLES, FULL_ADMIN_ROLES, POLICY_EDITOR_ROLES, Role } from "helpers/roles";
import { AdminPanelSettingsOutlined, CodeOutlined, FolderSharedOutlined, SettingsApplications, SupervisedUserCircleOutlined, PolicyOutlined, CloudDownload } from "@mui/icons-material";

export const MenuDefinition: Menu = {
    itens: [
        {
            type: "item",
            icon: <DashboardIcon />,
            text: "Dashboard",
            path: "/",
            roles: ALL_ROLES,
        },
        {
            type: "group",
            icon: <BarChartIcon />,
            text: "Activities",
            defaultPath: "/activities",
            itens: [
                {
                    type: "item",
                    icon: <AdminPanelSettingsOutlined />,
                    text: "Data Log",
                    path: "/activities",
                    roles: ALL_ROLES,
                },
                {
                    type: "item",
                    icon: <AlertLogIcon />,
                    text: "Alert Log",
                    path: "/alertlogs",
                    roles: ALL_ROLES,
                },
            ]
        },
        {
            type: "group",
            icon: <VerifiedUserOutlinedIcon />,
            text: "Policy",
            defaultPath: "/rules",
            itens: [
                {
                    type: "item",
                    icon: <AlertIcon />,
                    text: "Alerts",
                    path: "/alerts",
                    roles: ALL_ROLES,
                },
                {
                    type: "item",
                    icon: <RuleIcon />,
                    text: "Rules",
                    path: "/rules",
                    roles: ALL_ROLES,
                },
                {
                    type: "item",
                    icon: <AppsIcon />,
                    text: "Applications",
                    path: "/applications",
                    roles: ALL_ROLES,
                },
                {
                    type: "item",
                    icon: <DataObjectIcon />,
                    text: "Data Type",
                    path: "/datatypes",
                    roles: ALL_ROLES,
                },
                {
                    type: "item",
                    icon: <PasswordIcon />,
                    text: "Mask Formats",
                    path: "/maskformats",
                    roles: ALL_ROLES,
                },
                {
                    type: "item",
                    icon: <SettingsEthernetIcon />,
                    text: "Obfuscations",
                    path: "/obfuscations",
                    roles: ALL_ROLES,
                },
                {
                    type: "item",
                    icon: <GroupsIcon />,
                    text: "Groups",
                    path: "/groups",
                    roles: POLICY_EDITOR_ROLES,
                },
            ]
        },
        {

            type: "group",
            icon: <SettingsOutlinedIcon />,
            text: "Configuration",
            defaultPath: "/configuration",
            itens: [
                {
                    type: "item",
                    icon: <SettingsApplications />,
                    text: "General",
                    path: "/configuration",
                    roles: FULL_ADMIN_ROLES,
                },
                {
                    type: "item",
                    icon: <AdminPanelSettingsOutlined />,
                    text: "ICAP",
                    path: "/icap",
                    roles: FULL_ADMIN_ROLES,
                },
                {
                    type: "item",
                    icon: <PolicyOutlined />,
                    text: "Scanning Mode",
                    path: "/scanning-mode",
                    roles: FULL_ADMIN_ROLES,
                },
                {
                    type: "item",
                    icon: <SupervisedUserCircleOutlined />,
                    text: "Users",
                    path: "/users",
                    roles: FULL_ADMIN_ROLES,
                },
                {
                    type: "item",
                    icon: <FolderSharedOutlined />,
                    text: "Directory",
                    path: "/directory",
                    roles: FULL_ADMIN_ROLES,
                },
                {
                    type: "item",
                    icon: <SendIcon />,
                    text: "Notifications",
                    path: "/alertnotification",
                    roles: FULL_ADMIN_ROLES,
                },
                {
                    type: "item",
                    icon: <CodeOutlined />,
                    text: "API Keys",
                    path: "/apikeys",
                    roles: FULL_ADMIN_ROLES,
                },
                {
                    type: "item",
                    icon: <CloudDownload />,
                    text: "Import/Export Config",
                    path: "/import-export",
                    roles: FULL_ADMIN_ROLES,
                },
                {
                    type: "item",
                    icon: <StorageIcon />,
                    text: "Services",
                    path: "/services",
                    roles: FULL_ADMIN_ROLES,
                }
            ]
        }
    ]
};


export interface Menu {
    itens: (MenuItemRoute | MenuItemGroup)[];
}

export interface MenuItemRoute {
    type: "item"
    icon: React.ReactChild
    text: string
    path: string
    // NS-753
    //roles are the roles that see this item. It is required so that a new
    //item cannot appear for every role by omission
    roles: readonly Role[]
    newTab?: boolean
    badge?: (ctx: AuthContextData) => boolean
}

export interface MenuItemGroup {
    type: "group"
    icon: React.ReactChild
    text: string
    itens: MenuItemRoute[]
    defaultPath?: string
}

// NS-753
//menuForRole returns the menu that a role sees. A group without a visible item
//is removed. A group whose default page is hidden opens its first visible page.
export function menuForRole(menu: Menu, role: Role): Menu {
    const visible = (item: MenuItemRoute) => item.roles.includes(role);

    return {
        itens: menu.itens.flatMap((x): (MenuItemRoute | MenuItemGroup)[] => {
            if (x.type === "item") {
                return visible(x) ? [x] : [];
            }

            const itens = x.itens.filter(visible);
            if (!itens.length) {
                return [];
            }

            const defaultPath = x.defaultPath && !itens.some(i => i.path === x.defaultPath) ? itens[0].path : x.defaultPath;

            return [{ ...x, itens, defaultPath }];
        }),
    };
}
